import { Box, Typography, useTheme } from "@mui/material";
import { NextActionDto } from "../../types";
import { getFormattedDate, showToast } from "@/modules/common/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useCompleteNextAction } from "../../hooks/complete-next-action.hook";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import RoundCheckbox from "@/modules/common/components/ui/round-checkbox";

const NextActionCard = ({ nextAction }: { nextAction: NextActionDto }) => {
  const theme = useTheme();

  const date = getFormattedDate(new Date(nextAction.createdAt));

  const queryClient = useQueryClient();
  const { mutate, isPending } = useCompleteNextAction();

  const handleCompleteNextAction = () => {
    mutate(nextAction.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["track-type-stages", "active-track-stages"],
        });
        showToast({
          type: ToastType.SUCCESS,
          message: "Next action marked as completed",
          toastId: TOAST_IDS.UPDATE_NEXT_ACTION_SUCCESS,
          options: {
            position: "bottom-left",
            autoClose: 3000,
          },
        });
      },
      onError: () => {
        showToast({
          type: ToastType.ERROR,
          message: "Error marking next action as completed",
          toastId: TOAST_IDS.UPDATE_NEXT_ACTION_ERROR,
          options: {
            position: "bottom-left",
            autoClose: 3000,
          },
        });
      },
    });
  };

  return (
    <Box
      sx={{
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        px: 1,
        py: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 0.5,
        }}
      >
        <RoundCheckbox
          checked={nextAction.completedAt !== null}
          disabled={nextAction.completedAt !== null || isPending}
          onChange={handleCompleteNextAction}
        />

        <Typography
          variant="subtitle2"
          sx={{ ...theme.nawy.typography.english.variants.body.md }}
        >
          {nextAction.description} {nextAction.reminderFor?.name}
        </Typography>
      </Box>

      <Typography
        variant="caption"
        sx={{
          pl: 3,
          color:
            new Date() > nextAction.createdAt
              ? theme.nawy.colors.primitives.red[600]
              : "inherit",
        }}
      >
        {date}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          gap: 0.3,
          pl: 3,
        }}
      >
        <Typography
          variant="caption"
          color={theme.nawy.colors.semantic.text.secondary}
        >
          Added By{" "}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: theme.nawy.colors.primitives.blue[600],
            wordWrap: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "normal",
          }}
        >
          {nextAction.status.name}
        </Typography>
      </Box>
    </Box>
  );
};

export default NextActionCard;
